// frontend/src/components/forms/FuelLogForm.tsx
import React, { useState, useEffect, FormEvent, useRef } from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Interfaz para Activo (reutilizable)
interface AssetOption {
  id: number;
  name: string;
  identifier: string;
}

interface FuelLogFormProps {
  onLogCreated?: () => void; // Callback opcional
}

export function FuelLogForm({ onLogCreated }: FuelLogFormProps) {
  // Estado para los datos del formulario
  const [assetId, setAssetId] = useState<string>('');
  const [quantityLiters, setQuantityLiters] = useState<string>('');
  const [costPerLiter, setCostPerLiter] = useState<string>('');
  const [odometerReading, setOdometerReading] = useState<string>(''); // Opcional
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estado para cargar los activos y manejar UI
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Cargar lista de activos para el desplegable
  useEffect(() => {
    const fetchAssets = async () => {
      setLoadingAssets(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/api/data/assets`);
        if (!response.ok) throw new Error('No se pudieron cargar los activos.');
        const data: AssetOption[] = await response.json();
        setAssets(data);
      } catch (err) {
         setError(err instanceof Error ? err.message : 'Error desconocido cargando activos.');
        console.error("Error fetching assets for form:", err);
      } finally {
        setLoadingAssets(false);
      }
    };
    fetchAssets();
  }, []);

  // Handler para el input de archivo
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setAttachmentFile(e.target.files[0]);
    } else {
      setAttachmentFile(null);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!assetId || !quantityLiters || !costPerLiter) {
      setError("Debes seleccionar un activo y completar Litros y Costo/Litro.");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    // 1. Prepara el objeto de datos base
    const logDataObject = {
        asset_id: parseInt(assetId, 10),
        quantity_liters: parseFloat(quantityLiters) || 0,
        cost_per_liter: parseFloat(costPerLiter) || 0,
        odometer_reading: odometerReading ? parseInt(odometerReading, 10) : null,
    };

    // 2. Prepara cuerpo y cabeceras (Lógica Corregida)
    let finalRequestBody: FormData | string;
    let finalRequestHeaders: HeadersInit = {};

    if (attachmentFile) {
        const formDataBody = new FormData();
        Object.entries(logDataObject).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                formDataBody.append(key, String(value));
            }
        });
        formDataBody.append('attachmentFile', attachmentFile, attachmentFile.name);
        finalRequestBody = formDataBody;
        // No se necesitan cabeceras explícitas para FormData con fetch
    } else {
        finalRequestBody = JSON.stringify(logDataObject);
        finalRequestHeaders['Content-Type'] = 'application/json';
    }

    // 3. Realiza el fetch (usando las variables finales)
    try {
      const response = await fetch(`${API_BASE_URL}/api/data/fuel-logs`, {
        method: 'POST',
        headers: finalRequestHeaders,
        body: finalRequestBody,
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || `Error del servidor: ${response.statusText}`);
      }

      setSuccess(`Registro de combustible (ID: ${result.id}) creado exitosamente.`);
      setAssetId('');
      setQuantityLiters('');
      setCostPerLiter('');
      setOdometerReading('');
      setAttachmentFile(null);
      if (fileInputRef.current) {
           fileInputRef.current.value = '';
      }
      onLogCreated?.();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ocurrió un error desconocido.');
      console.error("Error en FuelLogForm submit:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <h4>Registrar Carga de Combustible</h4>
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      <Form.Group className="mb-3" controlId="fuelAssetId">
        <Form.Label>Activo</Form.Label>
        <Form.Select
          value={assetId}
          onChange={(e) => setAssetId(e.target.value)}
          required
          disabled={loadingAssets || isSubmitting}
          aria-label="Seleccionar Activo"
        >
          <option value="">{loadingAssets ? 'Cargando...' : '-- Selecciona un Activo --'}</option>
          {assets.map(asset => (
            <option key={asset.id} value={asset.id}>
              {asset.name} ({asset.identifier})
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Row>
        <Col>
            <Form.Group className="mb-3" controlId="fuelQuantity">
            <Form.Label>Litros Cargados</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={quantityLiters}
              onChange={(e) => setQuantityLiters(e.target.value)}
              required
              placeholder="Ej: 50.5"
              disabled={isSubmitting}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3" controlId="fuelCostPerLiter">
            <Form.Label>Costo por Litro ($)</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={costPerLiter}
              onChange={(e) => setCostPerLiter(e.target.value)}
              required
              placeholder="Ej: 1.15"
              disabled={isSubmitting}
            />
          </Form.Group>
        </Col>
      </Row>

       <Form.Group className="mb-3" controlId="fuelOdometer">
         <Form.Label>Odómetro / Horómetro (Opcional)</Form.Label>
         <Form.Control
           type="number"
           min="0"
           value={odometerReading}
           onChange={(e) => setOdometerReading(e.target.value)}
           placeholder="Ej: 125670"
           disabled={isSubmitting}
         />
       </Form.Group>

      <Form.Group controlId="fuelFormFile" className="mb-3">
        <Form.Label>Adjunto (Factura, etc. - Opcional)</Form.Label>
        <Form.Control
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={handleFileChange}
          disabled={isSubmitting}
          ref={fileInputRef}
        />
      </Form.Group>

      <Button variant="primary" type="submit" disabled={isSubmitting || loadingAssets}>
         {isSubmitting ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : null}
         {isSubmitting ? ' Registrando...' : 'Registrar Carga'}
      </Button>
    </Form>
  );
}